export type BanPeriod = '1d' | '3d' | '7d' | '30d' | 'permanent';

export type DateFormatter = (
  value: string | Date | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
) => string;

export const BAN_PERIOD_OPTIONS: { value: BanPeriod; label: string }[] = [
  { value: '1d', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'permanent', label: 'Permanent' },
];

const BAN_PERIOD_HOURS: Record<string, number> = {
  '1d': 24,
  '3d': 72,
  '7d': 168,
  '30d': 720,
};

export interface BanGroupFields {
  banned?: boolean;
  ban_period?: BanPeriod | string | null;
  ban_until?: string | null;
  ban_reason?: string | null;
  banned_at?: string | null;
  ban_active?: boolean;
}

function isBanGroupActive(group: BanGroupFields): boolean {
  if (group.ban_active != null) return Boolean(group.ban_active);
  if (!group.banned) return false;
  if (group.ban_period === 'permanent' || !group.ban_until) return true;
  return new Date() < new Date(group.ban_until);
}

export function isShopBanActive(user: {
  shop_ban_active?: boolean;
  shop_banned?: boolean;
  shop_ban_period?: string | null;
  shop_ban_until?: string | null;
}): boolean {
  return isBanGroupActive({
    ban_active: user.shop_ban_active,
    banned: user.shop_banned,
    ban_period: user.shop_ban_period,
    ban_until: user.shop_ban_until,
  });
}

export function isAdminBanActive(user: {
  admin_ban_active?: boolean;
  admin_banned?: boolean;
  admin_ban_period?: string | null;
  admin_ban_until?: string | null;
}): boolean {
  return isBanGroupActive({
    ban_active: user.admin_ban_active,
    banned: user.admin_banned,
    ban_period: user.admin_ban_period,
    ban_until: user.admin_ban_until,
  });
}

export function isEffectivelyBanned(user: Parameters<typeof isShopBanActive>[0] & Parameters<typeof isAdminBanActive>[0]): boolean {
  return isShopBanActive(user) || isAdminBanActive(user);
}

export function formatUnbanDisplay(
  banPeriod: string | null | undefined,
  banUntil: string | null | undefined,
  fmt: DateFormatter,
): string {
  if (banPeriod === 'permanent' || !banUntil) return 'Permanent';
  const d = new Date(banUntil);
  if (Number.isNaN(d.getTime())) return 'Permanent';
  return fmt(d);
}

export function previewUnbanFromPeriod(banPeriod: BanPeriod | '', fmt: DateFormatter): string | null {
  if (!banPeriod) return null;
  if (banPeriod === 'permanent') {
    return 'No automatic unban. A platform admin must remove this restriction manually.';
  }
  const hours = BAN_PERIOD_HOURS[banPeriod];
  if (!hours) return null;
  const until = new Date(Date.now() + hours * 60 * 60 * 1000);
  return `Unban scheduled: ${fmt(until)}`;
}
